import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { storageService } from '../services/storageService';
import { StorageFile, UploadProgress, StorageUsage, StorageFolder } from '../types/storage';
import JSZip from 'jszip';

const ContentManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'image' | 'template' | 'file' | 'graphic' | 'media'>('image');
  const [isUploading, setIsUploading] = useState(false);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [folders, setFolders] = useState<StorageFolder[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderName, setCurrentFolderName] = useState('All Files');
  const [folderMenuOpenId, setFolderMenuOpenId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [trashedFiles, setTrashedFiles] = useState<StorageFile[]>([]);
  const [trashedFolders, setTrashedFolders] = useState<StorageFolder[]>([]);
  const [isTrashView, setIsTrashView] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const [fileMenuOpenId, setFileMenuOpenId] = useState<string | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState<string | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [showEmptyFolderModal, setShowEmptyFolderModal] = useState(false);
  const [emptyFolderName, setEmptyFolderName] = useState('');
  const [moveToFolderFileId, setMoveToFolderFileId] = useState<string | null>(null);
  const [bulkMoveToFolderOpen, setBulkMoveToFolderOpen] = useState(false);
  const [allFolders, setAllFolders] = useState<StorageFolder[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const moveFolderButtonRef = React.useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const moveFolderArrowRef = React.useRef<{ [key: string]: SVGSVGElement | null }>({});
  const bulkMoveToFolderButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const bulkMoveToFolderArrowRef = React.useRef<SVGSVGElement | null>(null);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Load files, folders, and storage usage
  useEffect(() => {
    if (currentUser) {
      loadFiles();
      loadFolders();
      loadStorageUsage();
    }
  }, [currentUser, currentWorkspace, activeTab, currentFolderId]);

  useEffect(() => {
    if (!folderMenuOpenId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-folder-menu-container]')) {
        setFolderMenuOpenId(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFolderMenuOpenId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [folderMenuOpenId]);

  // Close file menu on outside click
  useEffect(() => {
    if (!fileMenuOpenId && !moveToFolderFileId && !bulkMoveToFolderOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-file-menu-container]') && !target.closest('[data-move-folder-dropdown]') && !target.closest('[data-bulk-move-folder-dropdown]')) {
        setFileMenuOpenId(null);
        setMoveToFolderFileId(null);
        setBulkMoveToFolderOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFileMenuOpenId(null);
        setMoveToFolderFileId(null);
        setBulkMoveToFolderOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fileMenuOpenId, moveToFolderFileId, bulkMoveToFolderOpen]);

  const loadFiles = async () => {
    if (!currentUser) return;
    
    try {
      const [userFiles, trashed] = await Promise.all([
        storageService.getFiles(
          currentUser.uid, 
          currentWorkspace?.id, 
          activeTab
        ),
        storageService.getTrashedFiles(
          currentUser.uid,
          currentWorkspace?.id,
          activeTab
        )
      ]);
      setFiles(userFiles);
      setTrashedFiles(trashed);
    } catch (error) {
      console.error('Error loading files:', error);
    }
  };

  const loadStorageUsage = async () => {
    if (!currentUser) return;
    
    try {
      const usage = await storageService.getStorageUsage(
        currentUser.uid, 
        currentWorkspace?.id
      );
      setStorageUsage(usage);
    } catch (error) {
      console.error('Error loading storage usage:', error);
    }
  };

  const loadFolders = async () => {
    if (!currentUser) return;
    
    try {
      const [userFolders, trashed, allUserFolders] = await Promise.all([
        storageService.getFolders(
          currentUser.uid,
          currentWorkspace?.id,
          currentFolderId
        ),
        storageService.getTrashedFolders(
          currentUser.uid,
          currentWorkspace?.id
        ),
        storageService.getFolders(
          currentUser.uid,
          currentWorkspace?.id,
          undefined // Get all folders regardless of parent
        )
      ]);
      setFolders(userFolders);
      setTrashedFolders(trashed);
      setAllFolders(allUserFolders);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!currentUser || !newFolderName.trim() || isCreatingFolder) return;

    setIsCreatingFolder(true);
    try {
      await storageService.createFolder(
        newFolderName.trim(),
        currentUser.uid,
        currentWorkspace?.id,
        currentFolderId || undefined
      );
      setNewFolderName('');
      setShowCreateFolderModal(false);
      await loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      const message = error instanceof Error ? error.message : 'Failed to create folder. Please try again.';
      alert(message);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleTrashFolder = async (folderId: string) => {
    if (!currentUser) return;

    try {
      await storageService.trashFolder(folderId, currentUser.uid, currentWorkspace?.id);
      await loadFolders();
      await loadFiles();
    } catch (error) {
      console.error('Error moving folder to trash:', error);
    }
  };

  const handleRestoreFolder = async (folderId: string) => {
    if (!currentUser) return;

    try {
      await storageService.restoreFolder(folderId, currentUser.uid, currentWorkspace?.id);
      await loadFolders();
      await loadFiles();
    } catch (error) {
      console.error('Error restoring folder:', error);
    }
  };

  const handlePermanentDeleteFolder = async (folderId: string) => {
    if (!currentUser) return;

    try {
      await storageService.permanentlyDeleteFolder(folderId, currentUser.uid, currentWorkspace?.id);
      await loadFolders();
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error permanently deleting folder:', error);
    }
  };

  const handleRenameFolder = async () => {
    if (!currentUser || !renameFolderId || !renameFolderName.trim()) return;

    setIsRenamingFolder(true);
    try {
      await storageService.updateFolder(
        renameFolderId,
        currentUser.uid,
        { name: renameFolderName.trim() }
      );
      setRenameFolderName('');
      setRenameFolderId(null);
      setShowRenameModal(false);
      await loadFolders();
    } catch (error) {
      console.error('Error renaming folder:', error);
      const message = error instanceof Error ? error.message : 'Failed to rename folder. Please try again.';
      alert(message);
    } finally {
      setIsRenamingFolder(false);
    }
  };

  const handleDownloadFolder = async (folder: StorageFolder) => {
    if (!currentUser) return;

    try {
      // Get all files in the folder
      const allFiles = await storageService.getFiles(
        currentUser.uid,
        currentWorkspace?.id,
        undefined,
        1000
      );
      const folderFiles = allFiles.filter(file => file.folderId === folder.id && !file.isTrashed);

      if (folderFiles.length === 0) {
        setEmptyFolderName(folder.name);
        setShowEmptyFolderModal(true);
        return;
      }

      // Create a new JSZip instance
      const zip = new JSZip();

      // Download and add each file to the zip
      for (const file of folderFiles) {
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          zip.file(file.originalName, blob);
        } catch (error) {
          console.error(`Error downloading file ${file.originalName}:`, error);
          // Continue with other files even if one fails
        }
      }

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Create download link and trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      link.href = url;
      link.download = `${folder.name}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading folder:', error);
      alert('Failed to download folder. Please try again.');
    }
  };

  const handleFolderMenuAction = (
    action: 'download' | 'rename' | 'info' | 'trash',
    folder: StorageFolder
  ) => {
    setFolderMenuOpenId(null);

    switch (action) {
      case 'download':
        handleDownloadFolder(folder);
        break;
      case 'rename':
        setRenameFolderId(folder.id!);
        setRenameFolderName(folder.name);
        setShowRenameModal(true);
        break;
      case 'info':
        alert(`Folder information for "${folder.name}" is coming soon.`);
        break;
      case 'trash':
        handleTrashFolder(folder.id!);
        break;
      default:
        break;
    }
  };

  const tabs = [
    { id: 'image', name: 'Images', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )},
    { id: 'template', name: 'Templates', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { id: 'file', name: 'Files', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    )},
    { id: 'graphic', name: 'Graphics', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    )},
    { id: 'media', name: 'Media Library', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )}
  ] as const;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0 || !currentUser) return;

    setIsUploading(true);
    const newUploadProgress: UploadProgress[] = [];

    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const progressId = `upload_${Date.now()}_${i}`;
        
        const initialProgress: UploadProgress = {
          fileId: progressId,
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        };

        setUploadProgress(prev => [...prev, initialProgress]);

        try {
          const uploadedFile = await storageService.uploadFile(
            file,
            currentUser.uid,
            currentWorkspace?.id,
            [],
            undefined,
            (progress) => {
              setUploadProgress(prev => 
                prev.map(p => p.fileId === progressId ? progress : p)
              );
            }
          );

          setUploadProgress(prev => 
            prev.map(p => p.fileId === progressId ? { ...p, status: 'completed', progress: 100 } : p)
          );

        } catch (error) {
          setUploadProgress(prev => 
            prev.map(p => p.fileId === progressId ? { 
              ...p, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed' 
            } : p)
          );
        }
      }

      await loadFiles();
      await loadStorageUsage();

    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress([]);
      }, 3000);
    }
  };

  const handleTrashFile = async (fileId: string) => {
    if (!currentUser) return;
    
    try {
      await storageService.trashFile(fileId, currentUser.uid, currentWorkspace?.id);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error moving file to trash:', error);
    }
  };

  const handleRestoreFile = async (fileId: string) => {
    if (!currentUser) return;

    try {
      await storageService.restoreFile(fileId, currentUser.uid, currentWorkspace?.id);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error restoring file:', error);
    }
  };

  const handlePermanentDeleteFile = async (fileId: string) => {
    if (!currentUser) return;

    try {
      await storageService.permanentlyDeleteFile(fileId, currentUser.uid, currentWorkspace?.id);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error permanently deleting file:', error);
    }
  };

  const handleDownloadFile = async (file: StorageFile) => {
    try {
      // Fetch the file from the URL
      const response = await fetch(file.url);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.originalName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleMoveFileToFolder = async (fileId: string, folderId: string | null) => {
    if (!currentUser) return;

    try {
      await storageService.moveFileToFolder(fileId, currentUser.uid, folderId);
      setMoveToFolderFileId(null);
      await loadFiles();
    } catch (error) {
      console.error('Error moving file to folder:', error);
      alert('Failed to move file. Please try again.');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      // Get all selected file objects
      const selectedFileObjects = files.filter(f => selectedFiles.includes(f.id!));
      
      if (selectedFileObjects.length === 0) {
        alert('No files found to download.');
        return;
      }

      // If only one file, download it directly
      if (selectedFileObjects.length === 1) {
        await handleDownloadFile(selectedFileObjects[0]);
        setSelectedFiles([]);
        return;
      }

      // For multiple files, create a ZIP
      const zip = new JSZip();
      
      // Fetch all files and add them to the ZIP
      for (const file of selectedFileObjects) {
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          zip.file(file.originalName, blob);
        } catch (error) {
          console.error(`Error fetching file ${file.originalName}:`, error);
        }
      }

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(zipBlob);
      link.href = url;
      link.download = `scubamail-files-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error downloading files:', error);
      alert('Failed to download files. Please try again.');
    }
  };

  const handleBulkMoveToFolder = async (folderId: string | null) => {
    if (!currentUser || selectedFiles.length === 0) return;

    try {
      for (const fileId of selectedFiles) {
        await storageService.moveFileToFolder(fileId, currentUser.uid, folderId);
      }
      setSelectedFiles([]);
      setBulkMoveToFolderOpen(false);
      await loadFiles();
    } catch (error) {
      console.error('Error moving files to folder:', error);
      alert('Failed to move files. Please try again.');
    }
  };

  const handleBulkTrash = async () => {
    if (!currentUser || selectedFiles.length === 0) return;
    
    try {
      for (const fileId of selectedFiles) {
        await storageService.trashFile(fileId, currentUser.uid, currentWorkspace?.id);
      }
      setSelectedFiles([]);
      await loadFiles();
      await loadStorageUsage();
    } catch (error) {
      console.error('Error moving files to trash:', error);
      alert('Failed to move files to trash. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStoragePercentage = (): number => {
    if (!storageUsage) return 0;
    return (storageUsage.totalSize / storageUsage.quota.totalStorage) * 100;
  };

  const formatTrashedTimestamp = (timestamp?: StorageFile['trashedAt']) => {
    if (!timestamp) return '';
    try {
      return timestamp.toDate().toLocaleString();
    } catch (error) {
      return '';
    }
  };

  const renderContent = () => {
    if (isTrashView) {
      const filteredTrashFolders = trashedFolders
        .filter(folder =>
          searchTerm === '' || folder.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
          const aTime = a.trashedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.trashedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

      const filteredTrashFiles = trashedFiles
        .filter(file => {
          const searchMatch = searchTerm === '' ||
            file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            file.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
          const typeMatch = activeTab ? file.type === activeTab : true;
          return searchMatch && typeMatch;
        })
        .sort((a, b) => {
          const aTime = a.trashedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.trashedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

      return (
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Trashed folders</h3>
              {filteredTrashFolders.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{filteredTrashFolders.length} item{filteredTrashFolders.length > 1 ? 's' : ''}</span>
              )}
            </div>
            {filteredTrashFolders.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTrashFolders.map((folder) => (
                  <div key={folder.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={folder.name}>{folder.name}</div>
                        {folder.trashedAt && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">Deleted {formatTrashedTimestamp(folder.trashedAt)}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => handleRestoreFolder(folder.id!)}
                        className="flex-1 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDeleteFolder(folder.id!)}
                        className="flex-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete permanently
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No folders in trash.
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Trashed files</h3>
              {filteredTrashFiles.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{filteredTrashFiles.length} item{filteredTrashFiles.length > 1 ? 's' : ''}</span>
              )}
            </div>
            {filteredTrashFiles.length > 0 ? (
              <div className="space-y-2">
                {filteredTrashFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-gray-500 dark:text-gray-400">
                        {file.type === 'image' ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12z" />
                          </svg>
                        ) : file.type === 'template' ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        ) : file.type === 'graphic' ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                          </svg>
                        ) : file.type === 'media' ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={file.originalName}>{file.originalName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(file.size)} • Deleted {formatTrashedTimestamp(file.trashedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRestoreFile(file.id!)}
                        className="px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500 rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDeleteFile(file.id!)}
                        className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete permanently
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No files in trash.
              </div>
            )}
          </div>
        </div>
      );
    }

    // Active view
    const filteredFiles = files.filter(file => {
      // Filter by folder if inside a folder
      const folderMatch = currentFolderId === null
        ? true // Show all files when viewing "All Files"
        : file.folderId === currentFolderId; // Show only files in current folder
      
      const searchMatch = searchTerm === '' || 
        file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return folderMatch && searchMatch;
    });

    const renderFileGrid = (files: StorageFile[]) => (
      files.length === 0 ? null : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {files.map((file) => (
              <div key={file.id} className="relative group">
                <div className={`aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors ${
                  selectedFiles.includes(file.id!) ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                }`}>
                  {file.type === 'image' ? (
                    <img 
                      src={file.url} 
                      alt={file.originalName}
                      className="w-full h-full object-cover rounded-lg cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage({ url: file.url, name: file.originalName });
                      }}
                    />
                  ) : (
                    <div className="text-center flex flex-col items-center justify-center h-full">
                      {file.type === 'template' ? (
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      ) : file.type === 'file' ? (
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      ) : file.type === 'graphic' ? (
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                        </svg>
                      ) : (
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                      <div className="text-xs text-gray-600 dark:text-gray-400 truncate px-1">
                        {file.originalName}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="absolute top-2 left-2">
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file.id!)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFiles([...selectedFiles, file.id!]);
                      } else {
                        setSelectedFiles(selectedFiles.filter(id => id !== file.id));
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                {/* Three-dot action menu */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="relative" data-file-menu-container>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileMenuOpenId(fileMenuOpenId === file.id ? null : file.id!);
                      }}
                      className="p-1.5 bg-white dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-lg border border-gray-200 dark:border-gray-700"
                      title="File actions"
                    >
                      <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {fileMenuOpenId === file.id && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden z-20 py-1.5">
                        {file.type === 'image' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFileMenuOpenId(null);
                              setPreviewImage({ url: file.url, name: file.originalName });
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Preview
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileMenuOpenId(null);
                            handleDownloadFile(file);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveToFolderFileId(moveToFolderFileId === file.id ? null : file.id!);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center justify-between gap-3"
                          >
                            <div className="flex items-center gap-3">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              Move to folder
                            </div>
                            <svg 
                              ref={(el) => { moveFolderArrowRef.current[file.id!] = el; }}
                              className={`w-4 h-4 transition-transform duration-150 ${moveToFolderFileId === file.id ? 'rotate-90' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {moveToFolderFileId === file.id && (() => {
                            const arrowEl = moveFolderArrowRef.current[file.id!];
                            const rect = arrowEl?.getBoundingClientRect();
                            return (
                              <div 
                                className="fixed w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden z-50 py-1.5 max-h-64 overflow-y-auto"
                                data-move-folder-dropdown
                                style={{
                                  top: rect ? `${rect.top}px` : '50%',
                                  left: rect ? `${rect.right + 8}px` : '50%',
                                  transform: rect ? 'none' : 'translate(-50%, -50%)',
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFileMenuOpenId(null);
                                    setMoveToFolderFileId(null);
                                    handleMoveFileToFolder(file.id!, null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  All Files
                                </button>
                                {allFolders.filter(f => !f.isTrashed).length > 0 ? (
                                  allFolders.filter(f => !f.isTrashed).map((folder) => (
                                    <button
                                      key={folder.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFileMenuOpenId(null);
                                        setMoveToFolderFileId(null);
                                        handleMoveFileToFolder(file.id!, folder.id!);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                      </svg>
                                      {folder.name}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                                    No folders available
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileMenuOpenId(null);
                            handleTrashFile(file.id!);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Move to trash
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="truncate">{file.originalName}</div>
                  <div>{formatFileSize(file.size)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    );

    const renderFileList = (files: StorageFile[]) => (
      files.length === 0 ? null : (
        <div className="space-y-2">
          {files.map((file) => (
            <div key={file.id} className={`flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              selectedFiles.includes(file.id!) ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' : ''
            }`}>
              <input
                type="checkbox"
                checked={selectedFiles.includes(file.id!)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedFiles([...selectedFiles, file.id!]);
                  } else {
                    setSelectedFiles(selectedFiles.filter(id => id !== file.id));
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
              />
              <span className="text-gray-500 mr-3 flex-shrink-0">
                {file.type === 'template' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ) : file.type === 'file' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                ) : file.type === 'graphic' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.originalName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)} • {file.mimeType}</div>
              </div>
              <div className="relative" data-file-menu-container>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFileMenuOpenId(fileMenuOpenId === file.id ? null : file.id!);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="File actions"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {fileMenuOpenId === file.id && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden z-20 py-1.5">
                    {file.type === 'image' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFileMenuOpenId(null);
                          setPreviewImage({ url: file.url, name: file.originalName });
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Preview
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileMenuOpenId(null);
                        handleDownloadFile(file);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMoveToFolderFileId(moveToFolderFileId === file.id ? null : file.id!);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          Move to folder
                        </div>
                        <svg 
                          ref={(el) => { moveFolderArrowRef.current[file.id!] = el; }}
                          className={`w-4 h-4 transition-transform duration-150 ${moveToFolderFileId === file.id ? 'rotate-90' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      {moveToFolderFileId === file.id && (() => {
                        const arrowEl = moveFolderArrowRef.current[file.id!];
                        const rect = arrowEl?.getBoundingClientRect();
                        return (
                          <div 
                            className="fixed w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden z-50 py-1.5 max-h-64 overflow-y-auto"
                            data-move-folder-dropdown
                            style={{
                              top: rect ? `${rect.top}px` : '50%',
                              left: rect ? `${rect.right - 4}px` : '50%',
                              transform: rect ? 'none' : 'translate(-50%, -50%)',
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setFileMenuOpenId(null);
                                setMoveToFolderFileId(null);
                                handleMoveFileToFolder(file.id!, null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              All Files
                            </button>
                            {allFolders.filter(f => !f.isTrashed).length > 0 ? (
                              allFolders.filter(f => !f.isTrashed).map((folder) => (
                                <button
                                  key={folder.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFileMenuOpenId(null);
                                    setMoveToFolderFileId(null);
                                    handleMoveFileToFolder(file.id!, folder.id!);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                  </svg>
                                  {folder.name}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 text-center">
                                No folders available
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileMenuOpenId(null);
                        handleTrashFile(file.id!);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Move to trash
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )
    );

    const filteredByTab = filteredFiles.filter(f => f.type === activeTab);
    return viewMode === 'grid' ? renderFileGrid(filteredByTab) : renderFileList(filteredByTab);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#050B12] font-sans" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Main Content */}
      <div className="mx-auto max-w-[1400px] px-6 py-8 space-y-8">
        <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-gray-900 dark:text-white text-3xl font-bold">Your Content</h1>
            <p className="text-gray-700 dark:text-gray-300 text-base">Manage and organize your files, images, and templates</p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
            <button 
              onClick={() => setShowCreateFolderModal(true)}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800 rounded-full font-normal text-xs flex items-center justify-center gap-1.5 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Create new folder</span>
            </button>
            <label
              htmlFor="file-upload-header"
              className="px-4 py-1.5 text-white rounded-full font-normal text-xs bg-[#0C1B2B] hover:bg-[#11263f] transition-colors duration-200 cursor-pointer text-center"
            >
              Upload
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload-header"
                accept={activeTab === 'image' ? 'image/*' : activeTab === 'media' ? 'video/*,audio/*' : '*'}
                disabled={isUploading}
              />
            </label>
          </div>
        </div>


        {/* Files Grid/List */}
        <div className="pb-6">
              {/* Breadcrumb Navigation */}
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setCurrentFolderId(null);
                      setCurrentFolderName('All Files');
                      setFolderMenuOpenId(null);
                      setSelectedFolderId(null);
                    }}
                    className={`text-xl font-medium ${!isTrashView && currentFolderId === null ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}
                  >
                    All Files
                  </button>
                  {isTrashView && (
                    <>
                      <span className="text-gray-500 dark:text-gray-400 text-xl">/</span>
                      <span className="text-xl text-gray-900 dark:text-white font-semibold">Trash</span>
                    </>
                  )}
                  {currentFolderId && (
                    <>
                      <span className="text-gray-500 dark:text-gray-400 text-xl">/</span>
                      <span className="text-xl text-gray-900 dark:text-white font-medium">
                        {currentFolderName}
                      </span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none sm:w-72 lg:w-80">
                    <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={isTrashView ? 'Search trash' : 'Search files'}
                      className="w-full pl-9 pr-10 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        aria-label="Clear search"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* View Toggle Button */}
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-full transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                      title="Grid Layout"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-full transition-colors ${
                        viewMode === 'list'
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                      title="List Layout"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsTrashView(prev => !prev);
                      setFolderMenuOpenId(null);
                      setSelectedFiles([]);
                      setSelectedFolderId(null);
                      if (!isTrashView) {
                        setCurrentFolderId(null);
                        setCurrentFolderName('All Files');
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                      isTrashView
                        ? 'bg-red-600 text-white dark:bg-red-500'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800'
                    }`}
                    title={isTrashView ? 'Back to files' : 'View trash'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v1h18V5a2 2 0 00-2-2h-4m-4 0V2h-4v1" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14M10 11v6m4-6v6" />
                    </svg>
                    <span>{isTrashView ? 'Back to files' : 'Trash'}</span>
                  </button>
                </div>
              </div>

              <div className="mb-5 flex flex-wrap items-center gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white dark:bg-blue-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className={activeTab === tab.id ? 'text-white' : 'text-gray-500 dark:text-gray-400'}>
                      {tab.icon}
                    </span>
                    <span>{tab.name}</span>
                  </button>
                ))}
              </div>

              {storageUsage && (
                <div className="mb-6">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatFileSize(storageUsage.totalSize)} of {formatFileSize(storageUsage.quota.totalStorage)} used
                  </div>
                </div>
              )}

              {/* Folders Grid */}
              {!isTrashView && folders.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{currentFolderId === null ? 'Folders' : 'Subfolders'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {folders.map((folder) => {
                      const isMenuOpen = folderMenuOpenId === folder.id;

                      return (
                        <div
                          key={folder.id}
                          data-folder-menu-container
                          className={`group relative bg-white dark:bg-gray-800 rounded-lg border transition-all duration-200 cursor-pointer ${
                            selectedFolderId === folder.id
                              ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                          }`}
                          onClick={() => {
                            setFolderMenuOpenId(null);
                            setSelectedFolderId(folder.id!);
                          }}
                          onDoubleClick={() => {
                            setFolderMenuOpenId(null);
                            setCurrentFolderId(folder.id!);
                            setCurrentFolderName(folder.name);
                            setSelectedFolderId(null);
                          }}
                        >
                          <div className="p-4 flex items-center space-x-3 pr-10">
                            <svg className="w-8 h-8 text-gray-600 dark:text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z" />
                            </svg>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={folder.name}>
                              {folder.name}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuOpenId((prev) => (prev === folder.id ? null : folder.id ?? null));
                            }}
                            className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Folder actions"
                            aria-haspopup="menu"
                            aria-expanded={isMenuOpen}
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
                            </svg>
                          </button>

                          {isMenuOpen && (
                            <div
                              role="menu"
                              className="absolute top-12 right-3 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden z-20 py-1.5"
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFolderMenuAction('download', folder);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                role="menuitem"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFolderMenuAction('rename', folder);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                role="menuitem"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Rename
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFolderMenuAction('trash', folder);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 flex items-center gap-3"
                                role="menuitem"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Move to trash
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Bulk Actions Toolbar */}
              <div className="mb-6 h-12 flex items-center">
                {selectedFiles.length > 0 && (
                  <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Close/Deselect Button */}
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="flex-shrink-0 p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                      title="Deselect all"
                    >
                      <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    {/* Selected Count */}
                    <span className="text-sm text-gray-700 dark:text-gray-300 flex-shrink-0">
                      {selectedFiles.length} selected
                    </span>

                    {/* Action Icons */}
                    <div className="flex items-center gap-2 ml-auto">
                      {/* Download */}
                      <button
                        type="button"
                        onClick={handleBulkDownload}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                        title="Download"
                      >
                        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>

                      {/* Move to Folder */}
                      <div className="relative" data-bulk-move-folder-dropdown>
                        <button
                          type="button"
                          ref={bulkMoveToFolderButtonRef}
                          onClick={() => setBulkMoveToFolderOpen(!bulkMoveToFolderOpen)}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                          title="Move to folder"
                        >
                          <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13l3 3m0 0l3-3m-3 3V8" />
                          </svg>
                        </button>
                        {bulkMoveToFolderOpen && (
                          <div
                            className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 max-h-64 overflow-y-auto"
                            style={{
                              top: bulkMoveToFolderArrowRef.current
                                ? `${bulkMoveToFolderArrowRef.current.getBoundingClientRect().height + 8}px`
                                : '100%',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleBulkMoveToFolder(null)}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
                            >
                              All Files
                            </button>
                            {allFolders.length > 0 && (
                              <>
                                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                                {allFolders.map((folder) => (
                                  <button
                                    key={folder.id}
                                    type="button"
                                    onClick={() => handleBulkMoveToFolder(folder.id!)}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
                                  >
                                    {folder.name}
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Delete/Trash */}
                      <button
                        type="button"
                        onClick={handleBulkTrash}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                        title="Move to trash"
                      >
                        <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Content Grid */}
              {renderContent()}
            </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                } else if (e.key === 'Escape') {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }
              }}
              placeholder="Folder name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || isCreatingFolder}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingFolder ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rename Folder</h3>
            <input
              type="text"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameFolder();
                } else if (e.key === 'Escape') {
                  setShowRenameModal(false);
                  setRenameFolderName('');
                  setRenameFolderId(null);
                }
              }}
              placeholder="Folder name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRenameModal(false);
                  setRenameFolderName('');
                  setRenameFolderId(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameFolder}
                disabled={isRenamingFolder || !renameFolderName.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRenamingFolder ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty Folder Modal */}
      {showEmptyFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700">
                <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">Folder is Empty</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              The folder <span className="font-medium text-gray-900 dark:text-white">"{emptyFolderName}"</span> does not contain any files.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowEmptyFolderModal(false);
                  setEmptyFolderName('');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
              aria-label="Close preview"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={previewImage.url} 
              alt={previewImage.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
              <div className="text-sm font-medium truncate max-w-md">{previewImage.name}</div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Floating Box */}
      {uploadProgress.length > 0 && (() => {
        const allCompleted = uploadProgress.every(p => p.status === 'completed' || p.status === 'error');
        const completedCount = uploadProgress.filter(p => p.status === 'completed').length;
        const hasErrors = uploadProgress.some(p => p.status === 'error');
        
        return (
          <div className="fixed bottom-6 right-6 z-40 w-80 animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden transform transition-all duration-300">
              {allCompleted ? (
                // Completion Message
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-in zoom-in duration-300">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {completedCount} {completedCount === 1 ? 'file' : 'files'} uploaded
                      </div>
                      {hasErrors && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                          {uploadProgress.filter(p => p.status === 'error').length} failed
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setUploadProgress([])}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
                    aria-label="Close"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                // Progress View
                <>
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                      <span className="text-xs text-gray-900 dark:text-white">
                        Uploading {uploadProgress.length} {uploadProgress.length === 1 ? 'file' : 'files'}
                      </span>
                    </div>
                    <button
                      onClick={() => setUploadProgress([])}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5"
                      aria-label="Close"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="p-2.5 space-y-2">
                      {uploadProgress.map((progress, index) => (
                        <div 
                          key={progress.fileId} 
                          className="space-y-1 animate-in fade-in slide-in-from-right-2 duration-300"
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1 mr-2" title={progress.fileName}>
                              {progress.fileName}
                            </span>
                            <span className={`text-xs flex-shrink-0 transition-colors duration-300 ${
                              progress.status === 'error' ? 'text-red-600 dark:text-red-400' : 
                              progress.status === 'completed' ? 'text-green-600 dark:text-green-400' : 
                              'text-blue-600 dark:text-blue-400'
                            }`}>
                              {progress.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ease-out ${
                                progress.status === 'error' ? 'bg-red-500' : 
                                progress.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                              }`}
                              style={{ 
                                width: `${progress.progress}%`,
                                transition: 'width 0.3s ease-out'
                              }}
                            ></div>
                          </div>
                          {progress.status === 'error' && progress.error && (
                            <div className="text-xs text-red-600 dark:text-red-400 truncate animate-in fade-in duration-300">
                              {progress.error}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ContentManager;
